import { compose, withProps } from 'recompose'
import { InputText } from './index'
import toReduxFormField from '../../../enhancers/ToReduxFormField'
import { formatDateUTC as formatDateUtil } from 'sidecar-utility-library'

// Expected date format is MM/DD/YYYY
export const formatDate = value => {
  // If we get a YYYY-MM-DD, formats correctly
  const dobFormatRegex = /(\d{4})-(\d{2})-(\d{2})$/gm

  if (dobFormatRegex.test(value)) {
    return value.replace(dobFormatRegex, '$2/$3/$1')
  }

  // If we get a ISO format from API, formats correctly
  const apiFormatRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/gm
  if (apiFormatRegex.test(value)) {
    return formatDateUtil('MM/DD/YYYY', value)
  }

  // If the user modifies digits within sections, it will pass the validation,
  // because we don't want to re-render all the time, which cause cursor jumping.
  if (!value) return ''
  if (/^\d{1,2}\/\d{1,2}\/\d{1,4}$/.test(value)) return value

  // Formatting
  const clean = (value.match(/\d/g) || []).join('')
  let arr = clean.split('')

  // if MM starts with value > 1, we auto prefix with 0
  arr[0] > 1 && arr.splice(0, 0, '0')
  // if MM is over 12, cut off the last number entered
  arr[0] === '1' && arr[1] > 2 && arr.splice(1, 1)

  // if DD starts with value > 3, prefix with 0
  arr[2] > 3 && arr.splice(2, 0, '0')
  // if DD is over 31, prefix first number with 0
  // moving second number to YYYY where the next
  // set of logic will take over. 11/35 => 11/03/5
  arr[2] === '3' && arr[3] > 1 && arr.splice(2, 0, '0')

  // when we have at least one year number, we can
  // make a broad guess that we know the century. This
  // only works when the number we have isn't 1 or 2
  // because the user could just be typing out "19" or "20".
  if (arr.length === 5) {
    const y = Number(arr[4])
    y > 2 && arr.splice(4, 0, '1', '9')
    y === 0 && arr.splice(4, 0, '2', '0')
  }

  // when we have at least 2 numbers for the year we
  // have a better idea what century to prefix with.
  // This only works if the numbers we have are NOT
  // 19 or 20 because century would still be vague.
  // at this point, user should just enter the full year.
  if (arr.length > 4) {
    const yy = Number(`${arr[4]}${arr[5]}`)
    yy < 19 && arr.splice(4, 0, '2', '0')
    yy > 20 && arr.splice(4, 0, '1', '9')
  }

  // lastly, if the year is in the wrong century just
  // take what is good and throw away the rest.
  if (arr[4] > 2) arr = arr.slice(0, 4)
  if (arr[5] > 0 && arr[5] < 9) arr = arr.slice(0, 5)

  // add delimiters and do a final trim.
  arr.length > 2 && arr.splice(2, 0, '/')
  arr.length > 5 && arr.splice(5, 0, '/')
  arr.length > 10 && arr.splice(10, 100)

  return arr.join('')
}

export const parseDate = useDashes => value => {
  const dateFormatRegex = /(\d{2})\/(\d{2})\/(\d{4})$/gm
  if (!dateFormatRegex.test(value)) return value
  return useDashes
    ? value.replace(dateFormatRegex, '$3-$1-$2')
    : new Date(
        new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000,
      ).toISOString()
}

const enhance = compose(
  withProps(({ useDashes }) => ({
    format: formatDate,
    parse: parseDate(useDashes),
  })),
  toReduxFormField('InputDate'),
)

export default enhance(InputText)
